import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { Layout } from '@/components/layout/Layout';
import { PageSeoHead } from '@/components/seo/PageSeoHead';
import { StructuredData } from '@/components/seo/StructuredData';
import { ArticleContent } from '@/components/blog/ArticleContent';
import { useBlogArticle } from '@/hooks/useBlogArticles';
import { Skeleton } from '@/components/ui/skeleton';

const BASE_URL = 'https://sampaolicreazioni.it';

export default function BlogArticle() {
  const { slug } = useParams<{ slug: string }>();
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'en' ? 'en-GB' : 'it-IT';

  const { data: article, isLoading } = useBlogArticle(slug);

  if (isLoading) {
    return (
      <Layout>
        <section className="pt-28 md:pt-36 pb-8 bg-charcoal text-ivory">
          <div className="container-narrow">
            <Skeleton className="h-4 w-32 mb-4 bg-cream/20" />
            <Skeleton className="h-3 w-24 mb-2 bg-cream/20" />
            <Skeleton className="h-10 w-full bg-cream/20" />
          </div>
        </section>
        <section className="section-padding bg-background">
          <div className="container-narrow space-y-4">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        </section>
      </Layout>
    );
  }

  if (!article) {
    return (
      <Layout>
        <div className="pt-32 pb-20 text-center container-narrow">
          <h1 className="font-heading text-2xl mb-4">{t('notFound.title')}</h1>
          <Link to="/blog" className="text-bronze hover:underline">
            ← {t('blog.backToBlog')}
          </Link>
        </div>
      </Layout>
    );
  }

  const articleUrl = `${BASE_URL}/blog/${article.handle}`;
  const imageUrl = article.image?.url ?? '';

  return (
    <Layout>
      <PageSeoHead
        title={article.seo.title || `${article.title} | Sampaoli Creazioni`}
        description={article.seo.description || article.excerpt || ''}
        path={`/blog/${article.handle}`}
        image={imageUrl}
        imageAlt={article.image?.altText || article.title}
        type="article"
      />

      <StructuredData
        type="breadcrumb"
        items={[
          { name: 'Home', url: '/' },
          { name: 'Blog', url: '/blog' },
          { name: article.title, url: `/blog/${article.handle}` },
        ]}
      />

      <StructuredData
        type="article"
        title={article.title}
        description={article.seo.description || article.excerpt || ''}
        url={articleUrl}
        image={imageUrl}
        publishedAt={article.publishedAt}
        author="Andrea Sampaoli"
      />

      {/* Hero */}
      <section className="pt-28 md:pt-36 pb-8 bg-charcoal text-ivory">
        <div className="container-narrow">
          <Link
            to="/blog"
            className="inline-flex items-center gap-1 font-body text-sm text-cream/70 hover:text-cream mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('blog.backToBlog')}
          </Link>
          <time
            dateTime={article.publishedAt}
            className="block font-body text-xs text-bronze mb-2"
          >
            {new Date(article.publishedAt).toLocaleDateString(lang, {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </time>
          <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold leading-tight">
            {article.title}
          </h1>
        </div>
      </section>

      {/* Featured image */}
      {article.image && (
        <div className="container-narrow -mt-0 md:-mt-0">
          <img
            src={article.image.url}
            alt={article.image.altText || article.title}
            className="w-full rounded-lg shadow-lg mt-6"
          />
        </div>
      )}

      {/* Content */}
      <section className="section-padding bg-background">
        <div className="container-narrow">
          <ArticleContent html={article.contentHtml} />
        </div>
      </section>

      {/* Tags */}
      {article.tags.length > 0 && (
        <section className="pb-12 bg-background">
          <div className="container-narrow flex flex-wrap gap-2">
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="text-xs font-body px-3 py-1 rounded-full bg-secondary text-muted-foreground"
              >
                {tag}
              </span>
            ))}
          </div>
        </section>
      )}
    </Layout>
  );
}
